<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-file-upload" class="q-mb-sm" /> {{ $t('import') }}
		</div>

		<div class="row gutter-sm">
			<div class="col-xs-12 col-sm-12">
				<q-input
					v-model="title"
					:float-label="$t('title')"
					:error="$v.title.$error"
					@input="$v.title.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="import_type"
					:options="[
						{ label: $t('users'), value: 1 },
						{ label: $t('companies_and_branches'), value: 2 },
					]"
					:float-label="$t('import_type')"
					:error="$v.import_type.$error"
				/>
			</div>
		</div>

		<div class="row gutter-sm q-mt-sm">
			<div class="col-12">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('file') }}</p>
						<uploader
							content_type="xlsx"
							@fileUploaded="fileUploaded"
							:multiple="false"
							:error="$v.file_url.$error"
							:disable="id !== 0"
						/>

						<p v-if="file_url" class="q-mt-md file-url">
							<q-icon name="fal fa-check" color="positive" />
							<a :href="file_url" target="_blank"> {{ $truncateLink(file_url) }}</a>
						</p>
					</div>
				</div>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'admin_imports' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')" v-if="status != 2">
				<q-btn
					color="secondary"
					:disabled="disabled"
					class="full-width"
					:label="$t('save_and_validate')"
					icon="far fa-save"
					@click="saveData()"
				/>
			</div>

			<div
				class="col-xs-12 col-sm-3"
				:class="getOrder('import')"
				v-if="id != 0 && this.status == 3 && this.import == true"
			>
				<q-btn
					color="green"
					class="full-width"
					:label="$t('import')"
					icon="far fa-check"
					@click="importData()"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')" v-if="status == 2">
				<q-btn
					color="secondary"
					:disabled="disabled"
					class="full-width"
					:label="$t('process')"
					icon="far fa-save"
					@click="processImport()"
				/>
			</div>
		</div>

		<div v-if="validated" class="q-pt-md">
			<div class="q-pa-md q-pt-md bg-green-2">
				<p class="q-mb-none" v-if="users.length > 0">
					{{ $t('total_rows_found_for_users') }}: {{ users.length }}
				</p>
				<p class="q-mb-none" v-if="companies.length > 0">
					{{ $t('total_rows_found_for_companies') }}: {{ companies.length }}
				</p>
				<p class="q-mb-none" v-if="branches.length > 0">
					{{ $t('total_rows_found_for_branches') }}: {{ branches.length }}
				</p>
			</div>
		</div>

		<q-dialog v-model="show_message" @ok="hideError()" :cancel="false">
			<span slot="title">{{ $t('attention') }}</span>
			<span slot="message">{{
				$t(
					'wait_the_import_is_being_processed_you_will_be_notified_at_the_end_of_the_process'
				)
			}}</span>
		</q-dialog>

		<div v-if="table_users">
			<h4 class="q-my-md">{{ $t('all_users_errors') }}</h4>
			<q-table :data="users_errors" :columns="columns" :filter="filter" row-key="line">
				<q-tr slot="header" slot-scope="props" :props="props">
					<q-th v-for="col in props.cols" :key="col.name" :props="props">
						{{ $t(col.label) }}
					</q-th>
				</q-tr>
				<q-tr slot="body" slot-scope="props" :props="props">
					<q-td key="line" :props="props">{{ props.row.line }}</q-td>
					<q-td key="full_name" :props="props">{{ props.row.full_name }}</q-td>
					<q-td key="status_messages" :props="props">{{
						props.row.status_messages.map((e) => $t(e)).join(', ')
					}}</q-td>
					<q-td key="error_count" :props="props" auto-width>
						<q-chip
							small
							:icon="getIconStatus(props.row.error_count)"
							:color="getColorStatus(props.row.error_count)"
							class="full-width"
						>
							{{ $t(getStatus(props.row.error_count)) }}
						</q-chip>
					</q-td>
				</q-tr>
			</q-table>
		</div>

		<div v-if="table_companies">
			<h4 class="q-my-md">{{ $t('all_headquarters_errors') }}</h4>
			<q-table :data="companies_errors" :columns="columns" :filter="filter" row-key="line">
				<q-tr slot="header" slot-scope="props" :props="props">
					<q-th v-for="col in props.cols" :key="col.name" :props="props">
						{{ $t(col.label) }}
					</q-th>
				</q-tr>
				<q-tr slot="body" slot-scope="props" :props="props">
					<q-td key="line" :props="props">{{ props.row.line }}</q-td>
					<q-td key="full_name" :props="props">{{ props.row.company_name }}</q-td>
					<q-td key="status_messages" :props="props">{{
						props.row.status_messages.map((e) => $t(e)).join(', ')
					}}</q-td>
					<q-td key="error_count" :props="props" auto-width>
						<q-chip
							small
							:icon="getIconStatus(props.row.error_count)"
							:color="getColorStatus(props.row.error_count)"
							class="full-width"
						>
							{{ $t(getStatus(props.row.error_count)) }}
						</q-chip>
					</q-td>
				</q-tr>
			</q-table>
		</div>

		<div v-if="table_branches">
			<h4 class="q-my-md">{{ $t('all_store_errors') }}</h4>
			<q-table :data="branches_errors" :columns="columns" :filter="filter" row-key="line">
				<q-tr slot="header" slot-scope="props" :props="props">
					<q-th v-for="col in props.cols" :key="col.name" :props="props">
						{{ $t(col.label) }}
					</q-th>
				</q-tr>
				<q-tr slot="body" slot-scope="props" :props="props">
					<q-td key="line" :props="props">{{ props.row.line }}</q-td>
					<q-td key="full_name" :props="props">{{ props.row.branch_name }}</q-td>
					<q-td key="status_messages" :props="props">{{
						props.row.status_messages.map((e) => $t(e)).join(', ')
					}}</q-td>
					<q-td key="error_count" :props="props" auto-width>
						<q-chip
							small
							:icon="getIconStatus(props.row.error_count)"
							:color="getColorStatus(props.row.error_count)"
							class="full-width"
						>
							{{ $t(getStatus(props.row.error_count)) }}
						</q-chip>
					</q-td>
				</q-tr>
			</q-table>
		</div>

		<div v-if="logs.length">
			<h4 class="q-my-md">{{ $t('logs') }}</h4>
			<q-table
				:data="logs"
				@request="requestLog"
				:columns="columns_logs"
				:filter="filter"
				row-key="line"
				:pagination.sync="logs_pagination"
			>
				<q-tr slot="header" slot-scope="props" :props="props">
					<q-th v-for="col in props.cols" :key="col.name" :props="props">
						{{ $t(col.label) }}
					</q-th>
				</q-tr>
				<q-tr slot="body" slot-scope="props" :props="props">
					<q-td key="id" :props="props">{{ props.row.id }}</q-td>
					<q-td key="content" :props="props">{{ props.row.content }}</q-td>
					<q-td key="import_type" :props="props">
						{{ $t(getImportType(import_type)) }}
					</q-td>
					<!-- <q-td key="log_type" :props="props" auto-width>
						<q-chip
							small
							:icon="getIconImportLogType(props.row.log_type)"
							:color="getColorImportLogType(props.row.log_type)"
							class="full-width"
						>
							{{ $t(getImportLogType(props.row.log_type)) }}
						</q-chip>
					</q-td> -->
					<q-td key="status" :props="props" auto-width>
						<q-chip
							small
							:icon="getIconImportStatus(props.row.status)"
							:color="getColorImportStatus(props.row.status)"
							class="full-width"
						>
							{{ $t(getImportStatus(props.row.status)) }}
						</q-chip>
					</q-td>
				</q-tr>
			</q-table>
		</div>
	</q-page>
</template>

<script>
import Uploader from 'components/Uploader'
import { mapGetters } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {
	i18nOptions: { namespaces: ['custom', 'common', 'imports'] },

	data() {
		return {
			id: '',
			title: '',
			import_type: 1,
			file_url: '',
			status: '',
			users: [],
			users_errors: [],
			companies: [],
			companies_errors: [],
			branches: [],
			branches_errors: [],
			logs: [],
			filter: '',
			table_users: false,
			table_companies: false,
			table_branches: false,
			validated: false,
			import: false,
			disabled: false,
			show_message: false,
			logs_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: 5,
				descending: false,
				sortBy: null,
			},
			columns: [
				{
					name: 'line',
					required: true,
					label: 'line',
					align: 'left',
					field: 'line',
					sortable: true,
				},
				{
					name: 'full_name',
					required: true,
					label: 'name',
					align: 'left',
					field: 'full_name',
					sortable: true,
				},
				{
					name: 'status_messages',
					required: true,
					label: 'errors',
					align: 'left',
					field: 'status_messages',
					sortable: true,
				},
				{
					name: 'error_count',
					required: true,
					label: 'status',
					align: 'left',
					field: 'error_count',
					sortable: false,
				},
			],
			columns_logs: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'id',
					sortable: true,
				},
				{
					name: 'content',
					required: true,
					label: 'content',
					align: 'left',
					field: 'content',
					sortable: true,
				},
				{
					name: 'import_type',
					required: true,
					label: 'import_type',
					align: 'left',
					field: 'import_type',
					sortable: true,
				},
				// {
				// 	name: 'log_type',
				// 	required: true,
				// 	label: 'log_type',
				// 	align: 'left',
				// 	field: 'log_type',
				// 	sortable: true,
				// },
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: true,
				},
			],
		}
	},

	components: {
		uploader: Uploader,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			reload_content: 'common/reload_content',
			id_user_client_selected: 'common/id_user_client_selected',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},
	},

	methods: {
		async fetchData() {
			this.$q.loading.show()
			await this.$axios
				.get(`/admin_import/import_new/${this.id}/`, this.getHeader)
				.then((response) => {
					this.title = response.data.title
					this.import_type = response.data.import_type
					this.file_url = response.data.file_url
					this.status = response.data.status
					if (this.status === 1 || this.status === 4 || this.status === 2) {
						this.requestLog({ pagination: this.logs_pagination })
					}
					if (this.status != 2) {
						this.disabled = true
					}
					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		requestLog({ pagination }) {
			this.logs_pagination = pagination
			let data = {
				page_size: this.logs_pagination.rowsPerPage,
				page: this.logs_pagination.page,
			}
			this.$axios
				.get(
					`/admin_import/import_new/${this.id}/get_logs/?` + this.$qs.stringify(data),
					this.getHeader
				)
				.then((res) => {
					this.logs_pagination.rowsNumber = res.data.count

					this.logs = []
					res.data.results.forEach((result) => {
						this.logs.push({
							id: result.line,
							content: result.content,
							status: this.status,
						})
					})
				})
		},

		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				let fd = new FormData()
				fd.append('title', this.title)
				fd.append('import_type', this.import_type)
				fd.append('file_url', this.file_url)

				if (this.id != 0) {
					this.$axios
						.put(`/admin_import/import_new/${this.id}/`, fd, this.getHeader)
						.then((response) => {
							this.$q.loading.hide()
							this.id = response.data.id
							this.status = response.data.status
							this.$router.push({ name: 'admin_imports' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				} else {
					this.$axios
						.post(`/admin_import/import_new/`, fd, this.getHeader)
						.then((response) => {
							this.$q.loading.hide()
							this.id = response.data.id
							this.status = response.data.status
							this.$router.push({ name: 'admin_imports' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				}
			} else {
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fal fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		importData() {
			this.show_message = true
			let data = {}

			if (this.users.length > 0) {
				data = { users: this.users }
			} else {
				data = { companies: this.companies, branches: this.branches }
			}
			this.$axios
				.post(`/admin_import/import_new/${this.id}/start_import/`, data, this.getHeader)
				.then((response) => {
					setTimeout(this.$router.push({ name: 'admin_imports' }), 10)
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		processImport() {
			this.$q.loading.show()
			const data = {
				import_id: this.id,
				client: this.id_user_client_selected,
				json_index: 0,
			}
			this.$axios
				.post(`/admin_import/import_new/process_file/`, data, this.getHeader)
				.then((response) => {
					this.$q.loading.hide()
					this.$router.push({ name: 'admin_imports' })
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		imageUploaded(url) {
			this.image_url = url
			this.image_url_complete = url
		},

		fileUploaded(url) {
			this.file_url = url
			this.file_url_complete = url
			this.import = false
			this.disabled = false
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			} else if (name == 'validate') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			} else if (name == 'import') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		getErrorColor(state) {
			if (state) return 'negative'
			else return ''
		},

		getErrorClass(state) {
			if (state) return 'checkbox-error'
			else return ''
		},

		getStatus(status) {
			if (status > 0) return 'error'
		},

		getIconStatus(status) {
			if (status > 0) return 'fas fa-ban'
		},

		getColorStatus(status) {
			if (status > 0) return 'negative'
		},

		getImportStatus(status) {
			if (status == 1) return 'OK'
			else if (status == 2) return 'validated'
			else return 'error'
		},

		getIconImportStatus(status) {
			if (status == 1) return 'fas fa-check-double'
			else if (status == 2) return 'fas fa-check'
			else return 'fas fa-ban'
		},

		getColorImportStatus(status) {
			if (status == 1) return 'positive'
			else if (status == 2) return 'warning'
			else return 'negative'
		},

		getImportLogType(status) {
			if (status == 1) return 'inserted'
			else if (status == 2) return 'updated'
			else return 'deleted'
		},

		getColorImportLogType(status) {
			if (status == 1) return 'positive'
			else if (status == 2) return 'warning'
			else return 'negative'
		},

		// getIconImportLogType(status) {
		// 	if (status == 1) return 'fas fa-check'
		// 	else if (status == 2) return 'fas fa-pencil'
		// 	else return 'fas fa-ban'
		// },

		getImportType(type) {
			if (type == '1') return 'users'
			else return 'companies_and_branches'
		},
	},

	created() {
		this.id = this.$route.params.id
		if (this.id != 0) this.fetchData()
	},

	validations() {
		return {
			title: { required },
			import_type: { required },
			file_url: { required },
		}
	},

	watch: {
		$route: function(refreshPage) {
			this.id = this.$route.params.id
		},
		reload_content: function() {
			this.requestLog({
				pagination: this.logs_pagination,
			})
		},
	},
}
</script>

<style lang="stylus" scoped>
@import '~variables'

.file-url
    a
        text-decoration none
        color black
</style>
